import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { AppLink, IconButton, SegmentedFilter, Skeleton, cx, formatCount } from "@amber/ui";
import {
  FUNNEL_BREAKDOWNS,
  FUNNEL_COLUMNS,
  type FunnelBreakdown,
  type FunnelResponse,
  type HomeFrame,
} from "@amber/application/contracts";
import { FRAME_OPTIONS, FRAME_TEXT } from "./format";
import { funnelQuery } from "./queries";

const BREAKDOWN_LABELS: Record<FunnelBreakdown, string> = {
  person: "Person",
  platform: "Platform",
  language: "Language",
  family: "Source family",
};
const COLUMN_LABELS: Record<(typeof FUNNEL_COLUMNS)[number], string> = {
  found: "Found",
  archived: "Archived",
  finished: "Finished",
  context: "Context complete",
  attribution: "Attribution accepted",
  eligible: "Eligible",
  not_through: "Not through",
};

type FunnelRow = { key: string; label: string; cells: FunnelResponse["total"] };

function FunnelTableRow({ r, max }: { r: FunnelRow; max: number }) {
  const eligible = r.cells.eligible.count;
  const finishedOnly = r.cells.finished.count - eligible;
  const rest = r.cells.found.count - r.cells.finished.count;
  return (
    <tr className={cx("border-t border-divider", r.key === "__total" && "font-semibold")}>
      <th scope="row" className="py-1.5 pr-2 text-left font-strong">
        {r.label}
      </th>
      {FUNNEL_COLUMNS.map((c) => (
        <td key={c} className="px-2 py-1.5 text-right font-mono">
          <AppLink
            href={r.cells[c].href}
            aria-label={`${r.label}, ${COLUMN_LABELS[c]}: ${formatCount(r.cells[c].count)}`}
            className="text-link no-underline hover:underline"
          >
            {formatCount(r.cells[c].count)}
          </AppLink>
        </td>
      ))}
      <td className="py-1.5 pl-2">
        <span
          role="img"
          aria-label={`${formatCount(eligible)} eligible, ${formatCount(finishedOnly)} finished not eligible yet, ${formatCount(rest)} not finished`}
          className="flex h-2 w-[120px] overflow-hidden rounded-full bg-surface-2"
        >
          <span className="bg-link" style={{ width: `${String((eligible / max) * 100)}%` }} />
          <span className="bg-link/40" style={{ width: `${String((finishedOnly / max) * 100)}%` }} />
          <span className="bg-surface-3" style={{ width: `${String((rest / max) * 100)}%` }} />
        </span>
      </td>
    </tr>
  );
}

function FunnelTable({ data, by }: { data: FunnelResponse; by: FunnelBreakdown }) {
  const max = Math.max(1, ...data.rows.map((r) => r.cells.found.count), data.total.found.count);
  return (
    <table
      aria-label={`Coverage funnel by ${BREAKDOWN_LABELS[by].toLowerCase()}`}
      className="w-full border-collapse text-md"
    >
      <thead className="text-muted">
        <tr>
          <th className="py-1.5 pr-2 text-left font-normal">{BREAKDOWN_LABELS[by]}</th>
          {FUNNEL_COLUMNS.map((c) => (
            <th key={c} className="px-2 py-1.5 text-right font-normal">
              {COLUMN_LABELS[c]}
            </th>
          ))}
          <th className="py-1.5 pl-2 text-left font-normal">
            <span className="sr-only">Bar</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {[...data.rows, { key: "__total", label: "Total", cells: data.total }].map((r) => (
          <FunnelTableRow key={r.key} r={r} max={max} />
        ))}
      </tbody>
    </table>
  );
}

export function FunnelPanel({
  frame,
  onFrame,
  onClose,
}: {
  frame: HomeFrame;
  onFrame: (frame: HomeFrame) => void;
  onClose: () => void;
}) {
  const [by, setBy] = useState<FunnelBreakdown>("person");
  const query = useQuery(funnelQuery(frame, by));
  const data = query.data;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="funnel-title"
      className="fixed inset-0 z-menu flex justify-end bg-scrim"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="flex h-full w-[min(920px,100%)] flex-col bg-bg shadow-pop">
        <div className="flex items-start gap-3 border-b border-divider px-5 py-3">
          <div>
            <h2 id="funnel-title" className="m-0 text-xl font-semibold">
              Coverage funnel
            </h2>
            <p className="m-0 text-md text-muted">Communications, not copies · {FRAME_TEXT[frame]}</p>
          </div>
          <IconButton label="Close" size="sm" className="ml-auto" onClick={onClose} autoFocus>
            <X size={14} strokeWidth={1.8} aria-hidden="true" />
          </IconButton>
        </div>
        <div className="flex flex-wrap items-center gap-3 px-5 py-2.5">
          <SegmentedFilter
            label="Break down by"
            value={by}
            options={FUNNEL_BREAKDOWNS.map((b) => ({ value: b, label: BREAKDOWN_LABELS[b] }))}
            onChange={setBy}
          />
          <SegmentedFilter label="Time frame" value={frame} options={FRAME_OPTIONS} onChange={onFrame} />
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 pb-5">
          {data ? <FunnelTable data={data} by={by} /> : <Skeleton className="h-40 w-full" />}
          {data ? (
            <div className="mt-3 space-y-1 text-md text-muted">
              <p className="m-0">{data.note}</p>
              <p className="m-0">
                Context complete and Attribution accepted are the two evidence checks between Finished and Eligible; Not
                through is everything found but not eligible. Bars: eligible, finished not eligible yet, not finished,
                on one scale. No percentages.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
